export type BusinessTimeValue = {
	hour: number;
	minute: number;
};

export type BusinessWorkHour = {
	start: BusinessTimeValue;
	end: BusinessTimeValue;
};

export type BusinessWorkHourUpdateDto = {
	startTime: BusinessTimeValue;
	endTime: BusinessTimeValue;
};

const firstString = (...values: unknown[]) => values.find((value) => typeof value === 'string' && value.trim().length > 0) as string | undefined;

export const normalizeBusinessCity = (city: any, fallbackKey?: string | null) => {
	if (!city) return undefined;

	if (typeof city === 'string') {
		const faName = city.trim();
		return faName ? { faName, key: fallbackKey || '' } : undefined;
	}

	if (typeof city === 'object') {
		const faName = firstString(city.faName, city.FaName, city.name, city.Name, city.title, city.Title, city.key, city.Key);
		const key = firstString(city.key, city.Key, city.slug, city.Slug, fallbackKey);
		if (!faName && !key) return undefined;

		return {
			...city,
			...(faName ? { faName } : {}),
			...(key ? { key } : {}),
		};
	}

	return undefined;
};

export const getBusinessCityName = (city: any) => normalizeBusinessCity(city)?.faName || '';

const toTimeNumber = (value: unknown) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

export const normalizeBusinessTimeValue = (value: any): BusinessTimeValue | null => {
	if (!value) return null;

	if (typeof value === 'string') {
		const [hour, minute] = value.split(':');
		const parsedHour = toTimeNumber(hour);
		const parsedMinute = toTimeNumber(minute);

		if (parsedHour === null || parsedMinute === null) return null;
		return { hour: parsedHour, minute: parsedMinute };
	}

	if (typeof value === 'object') {
		const parsedHour = toTimeNumber(value.hour);
		const parsedMinute = toTimeNumber(value.minute);

		if (parsedHour === null || parsedMinute === null) return null;
		return { hour: parsedHour, minute: parsedMinute };
	}

	return null;
};

export const normalizeBusinessWorkHour = (workHour: any): BusinessWorkHour | null => {
	const start = normalizeBusinessTimeValue(workHour?.start ?? workHour?.startTime);
	const end = normalizeBusinessTimeValue(workHour?.end ?? workHour?.endTime);

	if (!start || !end) return null;
	return { start, end };
};

export const normalizeBusinessWorkHours = (businessInfo: any): BusinessWorkHour[] => {
	const normalizedWorkHours = Array.isArray(businessInfo?.workHours) ? businessInfo.workHours.map(normalizeBusinessWorkHour).filter(Boolean) : [];

	if (normalizedWorkHours.length) return normalizedWorkHours as BusinessWorkHour[];

	const legacyWorkHour = normalizeBusinessWorkHour({
		start: businessInfo?.startHoursOfWork,
		end: businessInfo?.endHoursOfWork,
	});

	return legacyWorkHour ? [legacyWorkHour] : [];
};

export const normalizeBusinessInfo = <T extends Record<string, any>>(businessInfo: T | null | undefined) => {
	if (!businessInfo || typeof businessInfo !== 'object') return {};
	const city = normalizeBusinessCity(
		businessInfo.city ?? businessInfo.City ?? businessInfo.cityName ?? businessInfo.CityName,
		businessInfo.citySlug ?? businessInfo.CitySlug ?? businessInfo.cityKey ?? businessInfo.CityKey,
	);

	return {
		...businessInfo,
		...(city ? { city } : {}),
		citySlug: businessInfo.citySlug ?? businessInfo.CitySlug ?? city?.key,
		workHours: normalizeBusinessWorkHours(businessInfo),
	};
};

export const mapBusinessWorkHoursToUpdateDto = (workHours: any[] = []): BusinessWorkHourUpdateDto[] =>
	workHours
		.map(normalizeBusinessWorkHour)
		.filter(Boolean)
		.map((workHour) => ({
			startTime: workHour!.start,
			endTime: workHour!.end,
		}));
